/**
 * What an inserted question is, shown at the top of its work column (pedagogy spec §5.3.6):
 * a contrast twin says what it changes compared with the previous problem and recalls the
 * prediction made for it (made before insertion, so only read here); a "similar problem" carries
 * its badge. Renders nothing for an ordinary question.
 */
import { Localized } from "@client/components/Localized";
import { Badge } from "@client/components/ui";
import { useT } from "@client/i18n";
import { PredictionChoice } from "./PredictionCard";
import type { FollowUpState } from "./session";

export interface FollowUpBannerProps {
  followUp: FollowUpState | undefined;
}

export function FollowUpBanner({ followUp }: FollowUpBannerProps) {
  const t = useT();
  if (!followUp) return null;

  if (followUp.kind === "similar") {
    return (
      <p className="mb-3">
        <Badge tone="neutral">{t("practice.similar.badge")}</Badge>
      </p>
    );
  }

  const prediction = followUp.prediction;
  return (
    <div
      role="group"
      aria-label={t("practice.contrast.title")}
      className="mb-4 rounded-lg border border-accent-300 bg-accent-50 px-3 py-2 text-sm text-ink-800 dark:border-accent-700 dark:bg-accent-950 dark:text-ink-100"
    >
      <p>
        <Badge tone="accent">{t("practice.contrast.badge")}</Badge>{" "}
        <span className="font-medium">{t("practice.contrast.eyebrow")}</span>{" "}
        {followUp.varies && <Localized value={followUp.varies} variant="inline" />}
      </p>
      {prediction && (
        <p className="mt-1">
          <span className="text-ink-600 dark:text-ink-300">{t("practice.prediction.label")}:</span>{" "}
          <strong>
            <PredictionChoice choice={prediction.choice} options={prediction.options} />
          </strong>
        </p>
      )}
    </div>
  );
}
